import React, { createContext, useState, useContext } from 'react';
import { toast } from 'react-hot-toast';

export type EducationLevel = 'high_school' | 'bachelors' | 'masters' | 'phd' | 'other';
export type StudyPreference = 'local' | 'global';

interface UserResponses {
  educationLevel: EducationLevel | '';
  subjects: string[];
  careerGoals: string[];
  longTermAspirations: string;
  timeCommitment: string;
  budget: string;
  studyPreference: StudyPreference | '';
  homeCountry?: string;
  hostCountry?: string;
}

interface QuestionsContextProps {
  userResponses: UserResponses;
  updateResponses: (responses: Partial<UserResponses>) => void;
  saveProgress: () => Promise<void>;
  loadProgress: () => Promise<void>;
  loading: boolean;
  resetResponses: () => void;
}

const initialResponses: UserResponses = {
  educationLevel: '',
  subjects: [],
  careerGoals: [],
  longTermAspirations: '',
  timeCommitment: '',
  budget: '',
  studyPreference: '',
};

const QuestionsContext = createContext<QuestionsContextProps | undefined>(undefined);

export const QuestionsProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [userResponses, setUserResponses] = useState<UserResponses>(initialResponses);
  const [loading, setLoading] = useState(false);

  const updateResponses = (responses: Partial<UserResponses>) => {
    setUserResponses((prev) => ({
      ...prev,
      ...responses,
    }));
  };

  const saveProgress = async () => {
    try {
      setLoading(true);
      // Save to localStorage instead of database
      localStorage.setItem('userResponses', JSON.stringify(userResponses));
      toast.success('Progress saved successfully');
    } catch (error) {
      console.error('Error saving progress:', error);
      toast.error('Failed to save progress');
    } finally {
      setLoading(false);
    }
  };

  const loadProgress = async () => {
    try {
      setLoading(true);
      // Load from localStorage instead of database
      const savedResponses = localStorage.getItem('userResponses');
      if (savedResponses) {
        setUserResponses(JSON.parse(savedResponses));
      }
    } catch (error) {
      console.error('Error loading progress:', error);
    } finally {
      setLoading(false);
    }
  };

  const resetResponses = () => {
    setUserResponses(initialResponses);
    localStorage.removeItem('userResponses');
  };

  const value = {
    userResponses,
    updateResponses,
    saveProgress,
    loadProgress,
    loading,
    resetResponses,
  };

  return <QuestionsContext.Provider value={value}>{children}</QuestionsContext.Provider>;
};

export const useQuestions = (): QuestionsContextProps => {
  const context = useContext(QuestionsContext);
  if (context === undefined) {
    throw new Error('useQuestions must be used within a QuestionsProvider');
  }
  return context;
};